import AddressModal from "@/components/AddressModal";
import type { AddressType } from "@/types/address";
import { createContext, useContext, useEffect, useState } from "react";

interface AddressModalConfig {
  addressToEdit?: AddressType;
}

interface AddressesContextType {
  addresses: AddressType[];
  addAddress: (address: AddressType) => void;
  updateAddress: (address: AddressType) => void;
  deleteAddress: (address: AddressType) => void;
  openAddressModal: (config: AddressModalConfig) => void;
  closeAddressModal: () => void;
  setActiveAddress: (address: AddressType) => void;
  activeAddress: AddressType | undefined;
}

const AddressesContext = createContext<AddressesContextType | undefined>(
  undefined
);

export const AddressesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [addresses, setAddresses] = useState<AddressType[]>(
    JSON.parse(localStorage.getItem("addresses") || "[]")
  );
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [addressToEdit, setAddressToEdit] = useState<AddressType | undefined>(
    undefined
  );
  const [activeAddress, setActiveAddress] = useState<AddressType | null>(
    JSON.parse(localStorage.getItem("activeAddress") || "null")
  );

  useEffect(() => {
    localStorage.setItem("addresses", JSON.stringify(addresses));
  }, [addresses]);

  useEffect(() => {
    localStorage.setItem("activeAddress", JSON.stringify(activeAddress));
  }, [activeAddress]);

  const addAddress = (address: AddressType) => {
    if (addresses.length === 0) {
      setActiveAddress(address);
    }

    setAddresses([...addresses, { ...address }]);
  };

  const updateAddress = (address: AddressType) => {
    setAddresses(addresses.map((a) => (a.id === address.id ? address : a)));
  };

  const deleteAddress = (address: AddressType) => {
    const updatedAddresses = addresses.filter((a) => a.id !== address.id);
    setAddresses(updatedAddresses);
    if (activeAddress?.id === address.id) {
      if (updatedAddresses.length > 0) {
        setActiveAddress(updatedAddresses[0]);
      } else {
        setActiveAddress(null);
      }
    }
  };

  const openAddressModal = (config: AddressModalConfig) => {
    setIsAddressModalOpen(true);
    setAddressToEdit(config.addressToEdit || undefined);
  };

  const closeAddressModal = () => {
    setIsAddressModalOpen(false);
    setAddressToEdit(undefined);
  };

  const onSubmit = (address: AddressType) => {
    console.log("SUBMITTED address", address);
    if (addressToEdit) {
      updateAddress(address);
    } else {
      addAddress({ ...address, id: addresses.length });
    }
    closeAddressModal();
  };

  return (
    <AddressesContext.Provider
      value={{
        addresses,
        addAddress,
        updateAddress,
        deleteAddress,
        openAddressModal,
        closeAddressModal,
        setActiveAddress,
        activeAddress: activeAddress ?? undefined,
      }}
    >
      {children}
      <AddressModal
        open={isAddressModalOpen}
        onClose={closeAddressModal}
        onSubmit={onSubmit}
        initialAddress={addressToEdit}
      />
    </AddressesContext.Provider>
  );
};

export const useAddresses = () => {
  const context = useContext(AddressesContext);
  if (!context) {
    throw new Error("useAddresses must be used within an AddressesProvider");
  }
  return context;
};
